import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCreateSubscriptionMutation, teachurKeys } from '@teachur/http-client';
import { getErrorMessage } from '@new/lib';
import type { SubscriptionOption } from '../constants';
import { useCreateStripeToken } from './useCreateStripeToken';
import type { SubscribeFormValues } from '../schemas/validations';

export const useSubscribe = (subscription: SubscriptionOption) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [couponPrices, setCouponPrices] = useState<{
    subscriptionId: string;
    price: number;
    subPrice: number;
  } | null>(null);

  const activeCoupon = couponPrices?.subscriptionId === subscription.id ? couponPrices : null;

  const price = activeCoupon?.price ?? subscription.price;
  const subPrice = activeCoupon?.subPrice ?? subscription.subPrice;

  const isFreeSubscription = price === 0 && subPrice === 0;

  const { mutateAsync: createToken, isPending: isTokenizing } = useCreateStripeToken({
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Card could not be processed.'));
    },
  });

  const { mutateAsync: createSubscription, isPending: isSubscribing } =
    useCreateSubscriptionMutation({
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: teachurKeys.users.currentUser() });
        navigate({ to: '/' });
      },
      onError: (error) => {
        toast.error(getErrorMessage(error, 'Subscription failed.'));
      },
    });

  const applyCoupon = (newPrice: number, newMonthlyPrice: number) => {
    setCouponPrices({
      subscriptionId: subscription.id,
      price: newPrice,
      subPrice: newMonthlyPrice,
    });
  };

  const isSubmittingRef = useRef(false);

  const submit = async (values: SubscribeFormValues) => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    const coupon = values.code || undefined;

    try {
      if (isFreeSubscription) {
        await createSubscription({ billingInfo: values, coupon });
        return;
      }

      let token;
      try {
        token = await createToken({ name: values.billingName });
      } catch {
        return;
      }

      await createSubscription({ billingInfo: values, token: token.id, coupon });
    } catch {
      // Errors are surfaced via the mutations' onError toasts.
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return {
    price,
    subPrice,
    isFreeSubscription,
    isSubmitting: isTokenizing || isSubscribing,
    applyCoupon,
    submit,
  };
};
